import { useState, useEffect } from 'react';

const VISIBLE_THRESHOLD = 0.05;
const BOTTOM_ROOT_MARGIN = '0px 0px -50px 0px';
const OBSERVER_SETUP_DELAY_MS = 1000;

/**
 * Enhanced useScrollFade hook with improved reliability
 * Fixes initial state and observer settings for better fade detection
 *
 * @param targetSelector - CSS selector for the element to observe (optional)
 * @param isDesktop - below desktop the element is always visible
 * @returns boolean - true if element should be visible, false if hidden
 */
export const useScrollFade = (targetSelector: string | undefined, isDesktop: boolean): boolean => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!isDesktop) {
            setIsVisible(true);
            return;
        }

        if (!targetSelector) {
            setIsVisible(false);
            return;
        }

        let observer: IntersectionObserver | undefined;

        const setupObserver = () => {
            const target = document.querySelector(targetSelector);

            if (!target) {
                console.warn(`useScrollFade: Target "${targetSelector}" not found, defaulting to visible`);
                setIsVisible(true);
                return;
            }

            observer = new IntersectionObserver(
                ([entry]) => {
                    setIsVisible(entry.isIntersecting);
                },
                {
                    threshold: VISIBLE_THRESHOLD,
                    rootMargin: BOTTOM_ROOT_MARGIN
                }
            );

            observer.observe(target);
        };

        // ✅ TIMING FIX: Delay setup to allow DOM settling
        const timeoutId = setTimeout(setupObserver, OBSERVER_SETUP_DELAY_MS);

        return () => {
            clearTimeout(timeoutId);
            observer?.disconnect();
        };
    }, [targetSelector, isDesktop]);

    return isVisible;
};
